import '@/styles/style.css';

import ui from '@nuxt/ui/vue-plugin';
import { setDefaultOptions } from 'date-fns';
import { ru } from 'date-fns/locale';
import { createPinia } from 'pinia';
import { createApp } from 'vue';
import * as ApolloComposable from '@vue/apollo-composable';

import App from '@/App.vue';
import { apolloClient } from '@/core/apollo/client';
import router from '@/core/router';
import { registerModules } from '@/core/register-modules';
import { setupValibotI18n } from '@/shared/valibot/i18n';

import { AuthModule } from '@/modules/auth';
import { HomeModule } from '@/modules/home';
import { DeclensionsModule } from '@/modules/declensions';
import { AdjectivesModule } from '@/modules/adjectives';
import { PronounsModule } from '@/modules/pronouns';
import { NumeralsModule } from '@/modules/numerals';
import { DatesModule } from '@/modules/dates';
import { VerbsModule } from '@/modules/verbs';

// Russian-only product: every date-fns call (format, formatDistanceToNow, …)
// defaults to the Russian locale unless the call passes its own.
setDefaultOptions({ locale: ru });

setupValibotI18n();

// AuthModule first: it owns `/login` and `/magic-link`, the two routes every
// redirect in the app points at.
registerModules({
  AuthModule,
  HomeModule,
  DeclensionsModule,
  AdjectivesModule,
  PronounsModule,
  NumeralsModule,
  DatesModule,
  VerbsModule,
});

const app = createApp(App);

app.use(createPinia());
app.use(router);
app.use(ui);
app.provide(ApolloComposable.ApolloClients, {
  default: apolloClient,
});

/**
 * Mount after the first navigation so the app never renders a route the auth
 * guard is about to redirect away from. Until then the boot state in
 * index.html holds the screen, and it says so if the wait runs long.
 *
 * The `catch` matters: `isReady()` rejects when the first navigation throws,
 * and without it the promise chain would stop here and leave that boot spinner
 * up forever. Mounting anyway hands the failure to the app, which has a
 * critical-error view for it.
 */
router.isReady()
  .catch((e: unknown) => {
    console.error('[main] The first navigation failed; mounting anyway:', e);
  })
  .finally(() => {
    app.mount('#app');
  });
